import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCounter } from "../../lib/functions";
import { Button, Input, message, Popconfirm, Table, TableProps } from "antd";
import { deleteQuestion, getQuestions } from "./action";
import { Link } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import { CiTrash } from "react-icons/ci";
import SectionTitle from "../../Components/SectionTitle";
import DashPagination from "../../Components/DashPagination";
import DashModal from "../../Components/DashModal";
import { PiPlus } from "react-icons/pi";
import AddNewQuestion from "./Components/AddNewQuestion";
import EditQuestion from "./Components/EditQuestion";
const { Search } = Input;

type Props = {
  addNewQuestion?: boolean;
  editQuestion?: boolean;
};

interface TableDataType {
  id: string;
  en: string;
  ar: string;
  fr: string;
  type: "S" | "B";
}

const Questions = (props: Props) => {
  const [data, setData] = useState<TableDataType[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("BettingWebsiteQAndA");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const questions = await getQuestions(pageSize, page, query);
      const formatted = questions.map((qs) => ({
        id: qs.id,
        en: qs.Question.En,
        ar: qs.Question.Ar,
        fr: qs.Question.Fr,
        type: qs.Type,
      }));
      setData(formatted);
    } catch (error) {
      message.error("Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    try {
      await deleteQuestion(id);
      message.success("Question deleted");
      fetchData();
      fetchCount();
    } catch (error) {
      message.error("Failed to delete question");
    }
  };

  const columns: TableProps<TableDataType>["columns"] = useMemo(
    () => [
      {
        title: "English",
        dataIndex: "en",
        key: "en",
      },
      {
        title: "Arabic",
        dataIndex: "ar",
        key: "ar",
      },
      {
        title: "French",
        dataIndex: "fr",
        key: "fr",
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (text) =>
          text === "S" ? <span>Text</span> : <span>Boolean</span>,
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => (
          <div className="flex gap-2">
            <Link
              to={`/questions/edit/${record.id}?page=${
                page + 1
              }&pageSize=${pageSize}`}
            >
              <Button type="link" icon={<EditOutlined />} />
            </Link>
            <Popconfirm
              title="Are you sure you want to delete this question?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="dashed" danger icon={<CiTrash />} />
            </Popconfirm>
          </div>
        ),
      },
    ],
    []
  );
  return (
    <div className="space-y-5">
      <SectionTitle title="Questions" />

      <div className="flex justify-between items-center">
        <Link to={`/questions/add?page=${page + 1}&pageSize=${pageSize}`}>
          <Button type="primary" icon={<PiPlus />}>
            Add New Question
          </Button>
        </Link>
        <Search
          allowClear
          placeholder="Search question..."
          className="w-1/5"
          onSearch={(value) => setQuery(value)}
        />
      </div>

      <Table<TableDataType>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
      />

      <DashPagination
        page={page}
        setPage={setPage}
        totalNumber={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      {props.addNewQuestion && (
        <DashModal
          title="Add New Question"
          width={800}
          cancleLink={`/questions?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewQuestion fetchData={fetchData} />
        </DashModal>
      )}

      {props.editQuestion && (
        <DashModal
          title="Edit Question"
          width={800}
          cancleLink={`/questions?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditQuestion fetchData={fetchData} />
        </DashModal>
      )}
    </div>
  );
};

export default Questions;
